import {
  useCallback,
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type WheelEvent as ReactWheelEvent,
} from "react";

import {
  clampCamera,
  fitCamera,
  panCameraBy,
  zoomCameraAt,
  type FramingViewportCamera,
} from "./framingViewportMath";

/**
 * Internal zoom/pan adapter (CROP-WB-007). The workbench depends only on
 * this interface and the normalized {rootId, centerX, centerY, scale}
 * camera, so a third-party pan/zoom library can be swapped in behind it
 * later without touching consumers. Implemented with Pointer Events and CSS
 * transforms; honors prefers-reduced-motion by never animating.
 */
export function FramingViewportAdapter({
  rootId,
  camera,
  onCameraChange,
  disabled,
  children,
  label,
}: {
  rootId: string;
  camera: FramingViewportCamera | null;
  onCameraChange: (camera: FramingViewportCamera) => void;
  disabled?: boolean;
  children: ReactNode;
  label: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    lastX: number;
    lastY: number;
  } | null>(null);
  const effective =
    camera && camera.rootId === rootId ? camera : fitCamera(rootId);

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (disabled) return;
      const container = containerRef.current;
      if (!container) return;
      event.preventDefault();
      const bounds = container.getBoundingClientRect();
      const focalX =
        effective.centerX +
        ((event.clientX - bounds.left) / bounds.width - 0.5) / effective.scale;
      const focalY =
        effective.centerY +
        ((event.clientY - bounds.top) / bounds.height - 0.5) / effective.scale;
      const factor = event.deltaY < 0 ? 1.2 : 1 / 1.2;
      onCameraChange(zoomCameraAt(effective, focalX, focalY, factor));
    },
    [disabled, effective, onCameraChange],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (disabled || event.button !== 0) return;
      dragRef.current = {
        pointerId: event.pointerId,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [disabled],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      const container = containerRef.current;
      if (!drag || !container || drag.pointerId !== event.pointerId) return;
      const bounds = container.getBoundingClientRect();
      const deltaX =
        (drag.lastX - event.clientX) / bounds.width / effective.scale;
      const deltaY =
        (drag.lastY - event.clientY) / bounds.height / effective.scale;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
      onCameraChange(panCameraBy(effective, deltaX, deltaY));
    },
    [effective, onCameraChange],
  );

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  }, []);

  // Lost pointer capture (browser-initiated): end the pan cleanly.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const cancel = () => {
      dragRef.current = null;
    };
    container.addEventListener("lostpointercapture", cancel);
    return () => container.removeEventListener("lostpointercapture", cancel);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      const pan = 0.08 / effective.scale;
      const actions: Record<string, () => FramingViewportCamera> = {
        ArrowLeft: () => panCameraBy(effective, -pan, 0),
        ArrowRight: () => panCameraBy(effective, pan, 0),
        ArrowUp: () => panCameraBy(effective, 0, -pan),
        ArrowDown: () => panCameraBy(effective, 0, pan),
        "+": () =>
          zoomCameraAt(effective, effective.centerX, effective.centerY, 1.2),
        "=": () =>
          zoomCameraAt(effective, effective.centerX, effective.centerY, 1.2),
        "-": () =>
          zoomCameraAt(
            effective,
            effective.centerX,
            effective.centerY,
            1 / 1.2,
          ),
        "0": () => fitCamera(rootId),
      };
      const action = actions[event.key];
      if (action) {
        event.preventDefault();
        onCameraChange(clampCamera(action()));
      }
    },
    [disabled, effective, onCameraChange, rootId],
  );

  const translateX = (0.5 - effective.centerX) * 100 * effective.scale;
  const translateY = (0.5 - effective.centerY) * 100 * effective.scale;

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label={`${label}. Zoom with the mouse wheel or plus and minus keys; pan with drag or arrow keys; press 0 to fit.`}
      tabIndex={0}
      className="relative h-full w-full touch-none select-none overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      data-viewport-scale={effective.scale.toFixed(2)}
    >
      <div
        className="h-full w-full"
        style={{
          transform: `translate(${translateX}%, ${translateY}%) scale(${effective.scale})`,
          transformOrigin: "center center",
          // Reduced-motion by design: camera moves are never animated.
          transition: "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
